import {
  ILineMetaNewRetention,
  IParsedChartDataRetention,
  IRetentionAverageReturnCell,
  IRetentionAverageStartCell,
  IRetentionCohortAverageHeader,
  IRetentionCohortHeader,
  IRetentionHeader,
  IRetentionOverTimeRow,
  IRetentionOverTimeTable,
  IRetentionRange,
  IRetentionReturnCell,
  IRetentionReturnPeriodHeader,
  IRetentionStandardRow,
  IRetentionStandardRowHeader,
  IRetentionStandardTable,
  IRetentionStartCell,
  IRetentionTable,
  RetentionHeaderType,
  RetentionPostfixOnColumnName,
  RetentionStandardRowType,
  RetentionTableType,
} from '../../components/Chart/interfaces';
import { getAllSlices, parseAliasReplacements, toAliasableSlice } from './util';
import {
  IRetentionIntervalFieldsFragment,
  IRetentionLiveFieldsFragment,
  IRetentionRowFieldsFragment,
  IRetentionSliceFieldsFragment,
  IReturnPeriodFieldsFragment,
  QueryType,
} from '../apollo/graphqlSchema';
import {
  IRetentionAnalysis,
  IRetentionAnalysisData,
} from '../apollo/responseTypes';
import { assertCondition, assertNotNil } from '../types';
import { UnreachableCaseError } from 'ts-essentials';
import { freeze } from '@reduxjs/toolkit';
import { isNil } from 'lodash';
import { globalFormat } from '../formatting';

export const getIntervalDisplay = (
  interval: IRetentionIntervalFieldsFragment,
) => {
  const { dateFormat } = globalFormat.getGlobalFormat();
  if (interval.start === interval.end) {
    return globalFormat.formatDate(interval.start, { dateFormat });
  } else {
    const formattedStart = globalFormat.formatDate(interval.start, {
      dateFormat,
    });
    const formattedEnd = globalFormat.formatDate(interval.end, { dateFormat });
    return `${formattedStart} - ${formattedEnd}`;
  }
};

export const getHeaderName = (header: IRetentionHeader) => {
  const { dateFormat } = globalFormat.getGlobalFormat();
  switch (header.type) {
    case RetentionHeaderType.Cohort:
      return globalFormat.formatDate(header.startInterval.start, {
        dateFormat,
      });
    case RetentionHeaderType.CohortAverage:
      return header.average.name;
    case RetentionHeaderType.ReturnPeriod:
      return header.returnPeriod.name;
    default:
      throw new UnreachableCaseError(header);
  }
};

const getSlicesByKey = (slices: IRetentionSliceFieldsFragment[]) => {
  const slicesByKey = new Map<string, IRetentionSliceFieldsFragment>();
  for (const slice of slices) {
    assertCondition(!slicesByKey.has(slice.key));
    slicesByKey.set(slice.key, slice);
  }

  return slicesByKey;
};

export const generateLinesMetaForRetentionTable = (
  table: IRetentionTable,
  slices: IRetentionSliceFieldsFragment[],
) => {
  const linesMeta: ILineMetaNewRetention = {};

  const slicesByKey = getSlicesByKey(slices);

  for (const row of table.rows) {
    const slice = slicesByKey.get(row.sliceKey);
    assertNotNil(slice);
    let rowName = getHeaderName(row.header);

    linesMeta[row.key] = {
      name: row.key,
      display: rowName,
      sliceKey: slice.key,
      sliceName: slice.name,
      measureKey: row.key,
      measureName: rowName,
      measureLabel: undefined,
      percentLineKey: row.key + RetentionPostfixOnColumnName.percentKey,
    };
  }
  return linesMeta;
};

const createStartCell = (
  startInterval: IRetentionIntervalFieldsFragment,
  actualInterval: IRetentionIntervalFieldsFragment,
  row: IRetentionRowFieldsFragment,
  live: IRetentionLiveFieldsFragment,
): IRetentionStartCell => {
  return {
    startInterval,
    actualInterval,
    startCount: row.startIdentities,
    live: live.cohortLive,
  };
};

const createReturnCell = (
  startInterval: IRetentionIntervalFieldsFragment,
  actualInterval: IRetentionIntervalFieldsFragment,
  returnInterval: IRetentionIntervalFieldsFragment,
  returnPeriod: IReturnPeriodFieldsFragment,
  row: IRetentionRowFieldsFragment,
  live: IRetentionLiveFieldsFragment,
  columnIndex: number,
): IRetentionReturnCell => {
  assertCondition(columnIndex < row.returnIdentities.length);
  assertCondition(columnIndex < row.churnIdentities.length);
  assertCondition(columnIndex < live.retentionLive.length);
  return {
    startInterval,
    actualInterval,
    returnInterval,
    returnPeriod,
    retentionCount: row.returnIdentities[columnIndex],
    retentionPercent: row.retentions[columnIndex],
    live: live.retentionLive[columnIndex],
    churnCount: row.churnIdentities[columnIndex],
    churnPercent: row.churns[columnIndex],
    __typename: 'IRetentionReturnCell',
  };
};

const createAverageStartCell = (
  row: IRetentionRowFieldsFragment,
): IRetentionAverageStartCell => ({
  startCount: row.startIdentities,
});

const createAverageReturnCell = (
  returnPeriod: IReturnPeriodFieldsFragment,
  row: IRetentionRowFieldsFragment,
  columnIndex: number,
): IRetentionAverageReturnCell => {
  assertCondition(columnIndex < row.returnIdentities.length);
  assertCondition(columnIndex < row.churnIdentities.length);
  return {
    returnPeriod,
    retentionCount: row.returnIdentities[columnIndex],
    retentionPercent: row.retentions[columnIndex],
    churnCount: row.churnIdentities[columnIndex],
    churnPercent: row.churns[columnIndex],
    __typename: 'IRetentionAverageReturnCell',
  };
};

const getSlicesByRowIndex = (slices: IRetentionSliceFieldsFragment[]) => {
  const slicesByRowIndex = new Map<number, IRetentionSliceFieldsFragment>();
  for (const slice of slices) {
    for (const rowIndex of slice.rowIndices) {
      assertCondition(!slicesByRowIndex.has(rowIndex));
      slicesByRowIndex.set(rowIndex, slice);
    }
  }

  return slicesByRowIndex;
};

const calculateRange = (
  rows: IRetentionStandardRow[] | IRetentionOverTimeRow[],
  key: 'retentionPercent' | 'churnPercent',
): IRetentionRange => {
  const range: IRetentionRange = {
    min: Number.MAX_SAFE_INTEGER,
    max: Number.MIN_SAFE_INTEGER,
  };
  for (const row of rows) {
    for (const returnProp of row.returns) {
      const value = returnProp[key];
      if (!isNil(value)) {
        range.min = Math.min(range.min, value);
        range.max = Math.max(range.max, value);
      }
    }
  }
  return range;
};

const generateStandardRetentionRows = (
  data: IRetentionAnalysisData,
  slices: IRetentionSliceFieldsFragment[],
): IRetentionStandardTable => {
  const { tableRowSelector, rows, retentionLive } = data;
  assertCondition(
    tableRowSelector?.__typename === 'RetentionStandardTableRowSelector',
  );

  const { standardColumns, standardRows, avgRetention } = tableRowSelector;
  const slicesByRowIndex = getSlicesByRowIndex(slices);

  const tableRows: IRetentionStandardRow[] = [];
  const rowHeaders: IRetentionStandardRowHeader[] = [];

  // Produce average rows first so they can appear on top
  const { rowIndices } = avgRetention;
  const averageRowHeader: IRetentionCohortAverageHeader = {
    type: RetentionHeaderType.CohortAverage,
    index: 0, // will renumber when joining the lists
    key: avgRetention.key,
    average: avgRetention,
  };
  rowHeaders.push(averageRowHeader);

  for (const rowIndex of rowIndices) {
    const row = rows[rowIndex];
    assertNotNil(row);
    const slice = slicesByRowIndex.get(rowIndex);
    assertNotNil(slice);
    tableRows.push({
      type: RetentionStandardRowType.Average,
      key: row.key,
      sliceKey: slice.key,
      header: averageRowHeader,
      start: createAverageStartCell(row),
      returns: row.returnIdentities.map((_, columnIndex) => {
        const returnPeriod = standardColumns[columnIndex];
        assertNotNil(returnPeriod);
        return createAverageReturnCell(returnPeriod, row, columnIndex);
      }),
    });
  }

  // ... produce cohort rows second
  for (
    let cohortStartIndex = 0;
    cohortStartIndex < standardRows.length;
    cohortStartIndex++
  ) {
    const {
      cohortStart: { startInterval, actualInterval, key },
      returnIntervals,
      rowIndices,
    } = standardRows[cohortStartIndex];

    const rowHeader: IRetentionCohortHeader = {
      type: RetentionHeaderType.Cohort,
      index: 0, // will renumber when joining the lists
      key,
      startInterval,
    };
    rowHeaders.push(rowHeader);

    const cohortRetentionLive = retentionLive[cohortStartIndex];
    assertNotNil(cohortRetentionLive);

    for (const rowIndex of rowIndices) {
      const row = rows[rowIndex];
      assertNotNil(row);
      const slice = slicesByRowIndex.get(rowIndex);
      assertNotNil(slice);
      tableRows.push({
        type: RetentionStandardRowType.Cohort,
        key: row.key,
        sliceKey: slice.key,
        header: rowHeader,
        start: createStartCell(
          startInterval,
          actualInterval,
          row,
          cohortRetentionLive,
        ),
        returns: row.returnIdentities.map((_, columnIndex) => {
          const returnInterval = returnIntervals[columnIndex];
          assertNotNil(returnInterval);
          const returnPeriod = standardColumns[columnIndex];
          assertNotNil(returnPeriod);
          return createReturnCell(
            startInterval,
            actualInterval,
            returnInterval,
            returnPeriod,
            row,
            cohortRetentionLive,
            columnIndex,
          );
        }),
      });
    }
  }

  // reindex row headers so they will follow the rows order
  rowHeaders.forEach((r, index) => (r.index = index));

  // Produce column headers (the cells above each column in the table)
  const columnHeaders = standardColumns.map(
    (returnPeriod, index): IRetentionReturnPeriodHeader => ({
      type: RetentionHeaderType.ReturnPeriod,
      key: returnPeriod.key,
      index,
      returnPeriod,
    }),
  );

  return {
    type: RetentionTableType.Standard,
    columnHeaders,
    rowHeaders,
    rows: tableRows,
    retentionRange: calculateRange(tableRows, 'retentionPercent'),
    churnRange: calculateRange(tableRows, 'churnPercent'),
  };
};

const generateRetentionOverTimeRows = (
  data: IRetentionAnalysisData,
  slices: IRetentionSliceFieldsFragment[],
): IRetentionOverTimeTable => {
  const { tableRowSelector, rows, retentionLive } = data;
  assertCondition(
    tableRowSelector?.__typename === 'RetentionOverTimeTableRowSelector',
  );

  const slicesByRowIndex = getSlicesByRowIndex(slices);
  const { overTimeRows, overTimeColumns } = tableRowSelector;

  const tableRows: IRetentionOverTimeRow[] = [];
  const rowHeaders: IRetentionReturnPeriodHeader[] = [];
  for (
    let returnPeriodIndex = 0;
    returnPeriodIndex < overTimeRows.length;
    returnPeriodIndex++
  ) {
    const overTimeRow = overTimeRows[returnPeriodIndex];
    const { returnPeriod, returnIntervals, rowIndices } = overTimeRow;

    const rowHeader: IRetentionReturnPeriodHeader = {
      type: RetentionHeaderType.ReturnPeriod,
      index: returnPeriodIndex,
      key: returnPeriod.key,
      returnPeriod,
    };
    rowHeaders.push(rowHeader);

    const returnPeriodRetentionLive = retentionLive[returnPeriodIndex];
    assertNotNil(returnPeriodRetentionLive);

    for (const rowIndex of rowIndices) {
      const row = rows[rowIndex];
      assertNotNil(row);
      const slice = slicesByRowIndex.get(rowIndex);
      assertNotNil(slice);
      tableRows.push({
        key: row.key,
        sliceKey: slice.key,
        header: rowHeader,
        returns: row.returnIdentities.map((_, columnIndex) => {
          const cohortStart = overTimeColumns[columnIndex];
          assertNotNil(cohortStart);
          const returnInterval = returnIntervals[columnIndex];
          assertNotNil(returnInterval);
          return createReturnCell(
            cohortStart.startInterval,
            cohortStart.actualInterval,
            returnInterval,
            returnPeriod,
            row,
            returnPeriodRetentionLive,
            columnIndex,
          );
        }),
      });
    }
  }

  // Produce column headers (the cells above each column in the table)
  const columnHeaders = overTimeColumns.map(
    ({ startInterval, key }, index): IRetentionCohortHeader => ({
      type: RetentionHeaderType.Cohort,
      key,
      index,
      startInterval,
    }),
  );

  return {
    type: RetentionTableType.OverTime,
    columnHeaders,
    rowHeaders,
    rows: tableRows,
    retentionRange: calculateRange(tableRows, 'retentionPercent'),
    churnRange: calculateRange(tableRows, 'churnPercent'),
  };
};

const emptyTable: IRetentionTable = freeze(
  {
    type: RetentionTableType.Standard,
    rows: [],
    columnHeaders: [],
    rowHeaders: [],
    retentionRange: { min: 0, max: 1 },
    churnRange: { min: 0, max: 1 },
  },
  true,
);

const generateRetentionTable = (
  data: IRetentionAnalysisData,
  slices: IRetentionSliceFieldsFragment[],
) => {
  if (data.isEmpty || isNil(data.tableRowSelector)) {
    return emptyTable;
  }
  switch (data.tableRowSelector.__typename) {
    case 'RetentionStandardTableRowSelector':
      return generateStandardRetentionRows(data, slices);
    case 'RetentionOverTimeTableRowSelector':
      return generateRetentionOverTimeRows(data, slices);
    default:
      throw new UnreachableCaseError(data.tableRowSelector);
  }
};

export default (analysis: IRetentionAnalysis): IParsedChartDataRetention => {
  const { data } = analysis;
  assertNotNil(data);

  const allSlices = getAllSlices(analysis).map(toAliasableSlice);
  const table = generateRetentionTable(data, allSlices);
  const linesMeta = generateLinesMetaForRetentionTable(table, allSlices);
  const aliasReplacements = parseAliasReplacements(
    analysis.pref.aliasReplacements,
  );

  return {
    queryType: QueryType.Retention,
    data: [],
    slices: allSlices,
    aliasReplacements,
    aliasReplacementsSnapshot: aliasReplacements,
    table,
    linesMeta,
  };
};
